import React, { useState, useEffect } from 'react';
import {
  Container, Box, Typography, Button, Chip, CircularProgress,
  Alert, Divider, Paper, Stack, Grid, IconButton, useTheme, useMediaQuery, Snackbar
} from '@mui/material';
import {
  LocationOn, Work, AttachMoney, OpenInNew, BusinessCenter,
  Verified, CheckCircleOutline, Share, Schedule, Description, List as ListIcon,
  HomeWork, Apartment, Payments
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import API_BASE_URL from '../Config/config';
import axios from 'axios';

// Standalone axios instance — no auth header required
const publicApi = axios.create({ baseURL: API_BASE_URL.endsWith('/') ? API_BASE_URL : `${API_BASE_URL}/` });

function PublicJobPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [gone, setGone] = useState(false);
  const [goneMsg, setGoneMsg] = useState('');
  const [error, setError] = useState('');
  const [shareSnackbarOpen, setShareSnackbarOpen] = useState(false);

  const primaryColor = '#1E3A8A';
  const secondaryColor = '#1E3A8A';

  const formatDate = (dateString) => {
    if (!dateString) return 'Recent';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(date);
  };

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    setShareSnackbarOpen(true);
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') return;
    setShareSnackbarOpen(false);
  };

  useEffect(() => {
    // Capture UTM params from the landing URL and store for the application step
    const params = new URLSearchParams(window.location.search);
    const utmKeys = ['source', 'utm_source', 'utm_medium', 'utm_campaign'];
    utmKeys.forEach(key => {
      const val = params.get(key);
      if (val) sessionStorage.setItem(`exihire_${key}`, val);
    });

    // Detect source from referrer when no UTM provided
    if (!params.get('source') && !params.get('utm_source') && document.referrer) {
      const ref = document.referrer.toLowerCase();
      let detected = 'direct';
      if (ref.includes('linkedin'))  detected = 'linkedin';
      else if (ref.includes('indeed'))   detected = 'indeed';
      else if (ref.includes('naukri'))   detected = 'naukri';
      else if (ref.includes('foundit'))  detected = 'foundit';
      else if (ref.includes('google'))   detected = 'google_jobs';
      sessionStorage.setItem('exihire_source', detected);
    }
    sessionStorage.setItem('exihire_referrer_url', document.referrer || '');
  }, []);

  useEffect(() => {
    async function fetchJob() {
      try {
        const res = await publicApi.get(`jobs/public/${slug}`);
        setJob(res.data);
        document.title = `${res.data.title} | ${res.data.company} | ExiHire`;
      } catch (err) {
        if (err.response?.status === 410) {
          setGone(true);
          setGoneMsg(err.response.data?.message || 'This job is no longer accepting applications.');
          const d = err.response.data;
          if (d?.title) setJob({ title: d.title, company: d.company });
        } else if (err.response?.status === 404) {
          setError('Job not found.');
        } else {
          setError('Unable to load job. Please try again later.');
        }
      } finally {
        setLoading(false);
      }
    }
    if (slug) fetchJob();
  }, [slug]);

  const handleApply = () => {
    if (!job) return;

    if (isAuthenticated) {
      if (user?.role === 'admin' || user?.role === 'super_admin') {
        alert('You are logged in as a recruiter. Please log in with a candidate account to apply.');
        return;
      }
      // Candidate is already logged in — go straight to the job application page
      navigate(`/jobs/${job.id}`);
      return;
    }

    // Not logged in — save intended destination and go to register
    sessionStorage.setItem('exihire_pending_job_id', job.id);
    sessionStorage.setItem('exihire_pending_job_slug', slug);
    navigate(`/register?redirect=/jobs/${job.id}`);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (gone) {
    return (
      <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
        <BusinessCenter sx={{ fontSize: 64, color: '#9ca3af', mb: 2 }} />
        {job?.title && (
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
            {job.title}
            {job.company && (
              <Typography component="span" variant="h5" sx={{ color: '#6b7280', fontWeight: 400 }}>
                {' '}at {job.company}
              </Typography>
            )}
          </Typography>
        )}
        <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
          {goneMsg}
        </Typography>
        <Button variant="outlined" onClick={() => navigate('/jobs')}>
          Browse Other Jobs
        </Button>
      </Container>
    );
  }

  const requirements = Array.isArray(job.requirements)
    ? job.requirements
    : typeof job.requirements === 'string'
      ? job.requirements.split('\n').filter(Boolean)
      : [];

  return (
    <Box sx={{ bgcolor: '#f8fafc', minHeight: '100vh', pb: 8 }}>
      {/* Hero Section */}
      <Box sx={{ bgcolor: 'white', borderBottom: '1px solid #e5e7eb', py: { xs: 4, md: 6 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography variant="h3" sx={{ fontWeight: 800, color: '#111827', mb: 1, fontSize: { xs: '2rem', md: '2.5rem' } }}>
                {job.title}
              </Typography>
              <Typography variant="h5" sx={{ color: primaryColor, fontWeight: 700, mb: 3 }}>
                {job.company}
              </Typography>

              <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap sx={{ color: '#4b5563' }}>
                {job.location && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocationOn sx={{ fontSize: '1.2rem', color: '#64748b' }} />
                    <Typography variant="body2" sx={{ fontWeight: 500, color: '#64748b' }}>{job.location}</Typography>
                  </Box>
                )}
                {job.workMode && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <HomeWork sx={{ fontSize: '1.2rem', color: '#64748b' }} />
                    <Typography variant="body2" sx={{ fontWeight: 500, color: '#64748b' }}>{job.workMode}</Typography>
                  </Box>
                )}
                {(job.salary || job.salary_range) && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Payments sx={{ fontSize: '1.2rem', color: '#64748b' }} />
                    <Typography variant="body2" sx={{ fontWeight: 500, color: '#64748b' }}>{job.salary || job.salary_range}</Typography>
                  </Box>
                )}
                {(job.experience || job.experience_range || job.experience_level) && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Work sx={{ fontSize: '1.2rem', color: '#64748b' }} />
                    <Typography variant="body2" sx={{ fontWeight: 500, color: '#64748b' }}>{job.experience || job.experience_range || job.experience_level}</Typography>
                  </Box>
                )}
              </Stack>
            </Grid>
            <Grid item xs={12} md={4}>
              <Stack direction="column" spacing={2} alignItems={{ xs: 'stretch', md: 'flex-end' }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleApply}
                  sx={{
                    background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
                    '&:hover': { 
                      background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
                      filter: 'brightness(1.1)',
                      transform: 'translateY(-2px)' 
                    },
                    fontWeight: 700,
                    px: 4,
                    py: 1.5,
                    borderRadius: '12px',
                    textTransform: 'none',
                    fontSize: '1rem',
                    boxShadow: `0 4px 14px ${primaryColor}40`,
                    transition: 'all 0.3s'
                  }}
                >
                  Apply for this position
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Share />}
                  onClick={handleShare}
                  sx={{
                    borderRadius: '12px',
                    textTransform: 'none',
                    fontWeight: 600,
                    borderColor: '#e5e7eb',
                    color: '#4b5563',
                    py: 1.2,
                    px: 4,
                    '&:hover': { bgcolor: '#f9fafb', borderColor: '#d1d5db' }
                  }}
                >
                  Share Job
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            {/* Job Description Card */}
            {job.description && (
              <Paper elevation={0} sx={{ p: { xs: 3, md: 4 }, mb: 3, border: '1px solid #e5e7eb', borderRadius: '16px', bgcolor: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 3 }}>
                  <Box sx={{ p: 1, bgcolor: '#f8fafc', color: '#4b5563', borderRadius: '8px', display: 'flex', border: '1px solid #e5e7eb' }}>
                    <Description fontSize="small" />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#111827' }}>
                    Job Description
                  </Typography>
                </Stack>
                <Divider sx={{ mb: 3 }} />
                <Box
                  sx={{
                    color: '#374151',
                    lineHeight: 1.8,
                    fontSize: '1rem',
                    '& ul, & ol': { pl: 3, mb: 2 },
                    '& li': { mb: 1.5 },
                    '& p': { mb: 2 },
                    '& h1, & h2, & h3': { color: '#111827', fontWeight: 700, mt: 3, mb: 2 }
                  }}
                  dangerouslySetInnerHTML={{ __html: job.description }}
                />
              </Paper>
            )}

            {/* Requirements Card */}
            {requirements.length > 0 && (
              <Paper elevation={0} sx={{ p: { xs: 3, md: 4 }, mb: 3, border: '1px solid #e5e7eb', borderRadius: '16px', bgcolor: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 3 }}>
                  <Box sx={{ p: 1, bgcolor: '#f8fafc', color: '#4b5563', borderRadius: '8px', display: 'flex', border: '1px solid #e5e7eb' }}>
                    <ListIcon fontSize="small" />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#111827' }}>
                    Requirements
                  </Typography>
                </Stack>
                <Divider sx={{ mb: 3 }} />
                <Stack spacing={2}>
                  {requirements.map((req, i) => (
                    <Stack direction="row" spacing={2} key={i} alignItems="flex-start">
                      <CheckCircleOutline sx={{ color: '#10b981', mt: 0.5, fontSize: '1.25rem' }} />
                      <Typography variant="body1" sx={{ color: '#374151', lineHeight: 1.6 }}>
                        {req}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Paper>
            )}
          </Grid>

          <Grid item xs={12} md={4}>
            {/* Sidebar Cards */}
            <Stack spacing={3}>
              <Paper elevation={0} sx={{ p: 3, border: '1px solid #e5e7eb', borderRadius: '16px', bgcolor: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, color: '#111827' }}>Job Overview</Typography>
                <Stack spacing={3}>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Posted on</Typography>
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 1 }}>
                      <Schedule sx={{ fontSize: '1.1rem', color: '#94a3b8' }} />
                      <Typography variant="body2" sx={{ fontWeight: 500, color: '#1e293b' }}>{formatDate(job.createdAt || job.created_at)}</Typography>
                    </Stack>
                    <Divider sx={{ mt: 2 }} />
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Employment Type</Typography>
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 1 }}>
                      <Work sx={{ fontSize: '1.1rem', color: '#94a3b8' }} />
                      <Typography variant="body2" sx={{ fontWeight: 500, color: '#1e293b' }}>{job.employmentType || job.jobType || job.job_type || 'Full-time'}</Typography>
                    </Stack>
                    <Divider sx={{ mt: 2 }} />
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Location</Typography>
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 1 }}>
                      <LocationOn sx={{ fontSize: '1.1rem', color: '#94a3b8' }} />
                      <Typography variant="body2" sx={{ fontWeight: 500, color: '#1e293b' }}>{job.location || 'Remote'}</Typography>
                    </Stack>
                    <Divider sx={{ mt: 2 }} />
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Salary Range</Typography>
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 1 }}>
                      <Payments sx={{ fontSize: '1.1rem', color: '#94a3b8' }} />
                      <Typography variant="body2" sx={{ fontWeight: 500, color: '#1e293b' }}>{job.salary || job.salary_range || 'Not disclosed'}</Typography>
                    </Stack>
                  </Box>
                </Stack>
              </Paper>
            </Stack>
          </Grid>
        </Grid>

        {/* Bottom Apply CTA */}
        <Paper
          elevation={0}
          sx={{
            mt: 3,
            p: { xs: 4, md: 6 },
            textAlign: 'center',
            borderRadius: '16px',
            bgcolor: 'white',
            border: '1px solid #e5e7eb',
            boxShadow: '0 1px 3px rgba(0,0,0,0.02)'
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 800, mb: 2, color: '#111827' }}>
            Ready to apply?
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, color: '#64748b', maxWidth: 600, mx: 'auto' }}>
            Join {job.company} and take the next step in your career. We're looking for talented individuals like you.
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={handleApply}
            sx={{
              background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
              '&:hover': { 
                background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
                filter: 'brightness(1.1)',
                transform: 'scale(1.02)' 
              },
              fontWeight: 700,
              px: 6,
              py: 2,
              borderRadius: '12px',
              textTransform: 'none',
              fontSize: '1.1rem',
              boxShadow: `0 4px 14px ${primaryColor}40`,
              transition: 'all 0.3s'
            }}
          >
            Apply Now
          </Button>
          <Typography variant="caption" sx={{ mt: 3, display: 'block', color: '#94a3b8' }}>
            Application takes less than 2 minutes
          </Typography>
        </Paper>
      </Container>

      <Snackbar
        open={shareSnackbarOpen}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity="success" 
          variant="filled" 
          icon={<CheckCircleOutline fontSize="inherit" />}
          sx={{ 
            width: '100%', 
            borderRadius: '12px',
            fontWeight: 600,
            bgcolor: '#10b981', // Emerald 500
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            '& .MuiAlert-icon': {
              color: 'white'
            }
          }}
        >
          Job link copied successfully
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default PublicJobPage;
